import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Turnstile } from "@marsidev/react-turnstile";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

const validationSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .min(2, "Please enter a valid name")
    .max(50, "Please enter a valid name")
    .required("Please specify your first name"),
  lastName: yup
    .string()
    .trim()
    .min(2, "Please enter a valid name")
    .max(50, "Please enter a valid name")
    .required("Please specify your last name"),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address")
    .required("Email is required."),
  company: yup
    .string()
    .trim()
    .min(1, "Please enter a valid company name")
    .max(50, "Please enter a valid company name"),
  message: yup
    .string()
    .trim()
    .max(1000, "The message cannot contain more than 1000 characters"),
});

const Form = ({ formData, setFormData, handleSubmit }) => {
  const { t } = useTranslation();
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaError, setCaptchaError] = useState("");
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: "",
    website: "",
  };

  const aa = () => {
    if (!captchaToken) {
      setCaptchaError("Please verify that you are not a robot.");
      return;
    }

    handleSubmit();
    formik.resetForm();
    setCaptchaToken("");
    setCaptchaKey((k) => k + 1);
    setCaptchaError(!turnstileSiteKey ? "Captcha is not configured. Please contact support." : "");
  };

  const formik = useFormik({
    initialValues: formData || initialValues,
    validationSchema: validationSchema,
    onSubmit: aa,
  });

  useEffect(() => {
    setFormData({ ...formik.values, captchaToken });
  }, [captchaToken, formik.values, setFormData]);

  useEffect(() => {
    if (!turnstileSiteKey) {
      setCaptchaError("Captcha is not configured. Please contact support.");
      return;
    }

    setCaptchaError("");
  }, [turnstileSiteKey]);

  return (
    <Box>
      <Box style={{ marginBottom: "20px" }}>
        <Typography color={"textSecondary"}>
          {t("form.completeForm")}
        </Typography>
        <Typography color={"textSecondary"}>
          {t("form.responseTime")}
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <TextField
            label="Website"
            name="website"
            autoComplete="off"
            tabIndex={-1}
            value={formik.values.website || ""}
            onChange={formik.handleChange}
          />
        </Box>
        <Grid container spacing={4}>
          <Grid item container xs={12} md={6} spacing={4}>
            <Grid item xs={12}>
              <TextField
                label={t("form.firstName")}
                variant="outlined"
                name={"firstName"}
                fullWidth
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("form.lastName")}
                variant="outlined"
                name={"lastName"}
                fullWidth
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("form.email")}
                variant="outlined"
                name={"email"}
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("form.company")}
                variant="outlined"
                name={"company"}
                fullWidth
                value={formik.values.company}
                onChange={formik.handleChange}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("form.message")}
              variant="outlined"
              name={"message"}
              fullWidth
              multiline
              rows={12}
              sx={{
                '& .MuiInputBase-root': {
                  height: '324px'
                }
              }}
              value={formik.values.message}
              onChange={formik.handleChange}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {turnstileSiteKey && (
              <Box sx={{ marginBottom: "8px" }}>
                <Turnstile
                  key={captchaKey}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => {
                    setCaptchaToken(token);
                    setCaptchaError("");
                  }}
                  onExpire={() => {
                    setCaptchaToken("");
                    setCaptchaError("Captcha expired. Please verify again.");
                  }}
                  onError={() => {
                    setCaptchaToken("");
                    setCaptchaError("Captcha failed to load. Please try again.");
                  }}
                />
              </Box>
            )}
            {captchaError && (
              <Typography color="error" variant="body2" sx={{ marginBottom: "12px" }}>
                {captchaError}
              </Typography>
            )}
            <Button
              size={"large"}
              variant={"contained"}
              type={"submit"}
              disabled={!captchaToken || !turnstileSiteKey}
            >
              {t("form.sendRequest")}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default Form;